import { takeLatest, spawn, put, call } from 'redux-saga/effects';

/* Project */
import apiRequest, { apiSuccess, post, update } from 'utils/api';
import * as APP from 'commons/commons.reducer';
import commonsTypes from 'commons/commons.types';
import controlledCall from 'utils/services/controlledSaga';
import {
  appActions,
  FETCH_INITIAL_STATE,
  FETCH_INITIAL_STATE_SUCCESS,
  FETCH_INITIAL_STATE_FAIL,
} from 'commons/commons.reducer';
import {
  DEFAULT_RETRY_CONFIG,
  calculateBackoffDelay,
  sleep,
  isRecoverableError,
  reportUploadError,
  reportUploadSuccess,
  UploadErrorType,
} from 'utils/retryUtils';

/**
 * Helper function to upload file to S3 using pre-signed URL
 *
 * Process:
 * - Uploads file to S3 using pre-signed PUT URL with 2-minute timeout
 * - Verifies success based on HTTP 200 OK status from S3
 *
 * @param {string} signedUrl - Pre-signed S3 URL for PUT operation
 * @param {File} file - File object to upload
 * @param {string} contentType - MIME type of the file
 * @param {string} fileName - Original file name for error messages
 * @returns {Promise<{success: boolean, error?: string, status?: number}>}
 */
async function uploadAndVerifyToS3(signedUrl, file, contentType, fileName) {
  try {
    console.log(`[S3] Starting upload: ${fileName} (${(file.size / 1024 / 1024).toFixed(2)} MB)`);

    // Upload file to S3 with timeout to detect slow/broken connections
    const uploadResponse = await Promise.race([
      fetch(decodeURI(signedUrl), {
        method: 'PUT',
        headers: {
          'Content-Type': contentType,
          'Access-Control-Allow-Headers': 'Content-Type',
          'Access-Control-Allow-Methods': 'OPTIONS,POST,GET',
          'Access-Control-Allow-Origin': '*',
        },
        body: file,
      }),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Upload timeout - conexión muy lenta o archivo muy grande')), 120000) // 2 minutes timeout
      )
    ]);

    if (!uploadResponse.ok) {
      console.error(`[S3] Upload failed: HTTP ${uploadResponse.status} - ${fileName}`);
      return {
        success: false,
        status: uploadResponse.status,
        error: `Error HTTP ${uploadResponse.status} al subir ${fileName}`,
        errorType: 'HTTPError'
      };
    }

    // HTTP 200 OK from S3 is sufficient guarantee that file was uploaded successfully
    console.log(`[S3] Upload successful: ${fileName} - HTTP ${uploadResponse.status}`);
    return {
      success: true,
      status: uploadResponse.status
    };
  } catch (error) {
    console.error(`[S3] Upload exception: ${fileName} -`, error);
    return {
      success: false,
      status: 0,
      error: error.message || `Error inesperado subiendo ${fileName}`,
      errorType: error.name || 'UnknownError'
    };
  }
}

function* postDiscrepancy() {
  yield takeLatest(commonsTypes.CREATE_DISCREPANCY, function* postDiscrepancyApi(action) {
    const payloadCopy = { ...action.payload };

    const { files, attachments } = payloadCopy;

    delete payloadCopy.files;
    delete payloadCopy.attachments;

    // Generate idempotency key to prevent duplicates
    const idempotencyKey = `${Date.now()}-${Math.random().toString(36).substring(7)}`;

    // Prepare file metadata for backend (send metadata, not actual files)
    const fileMetadata = [];
    const fileMapping = []; // Mapping between valid files and their metadata

    // Process main files - only add files that exist (no null values)
    const [publicFile, wordFile, confidentialFile] = files;
    if (publicFile) {
      fileMetadata.push({
        name: publicFile.name,
        size: publicFile.size,
        type: publicFile.type,
      });
      fileMapping.push({ type: 'public', file: publicFile });
    }
    if (wordFile) {
      fileMetadata.push({
        name: wordFile.name,
        size: wordFile.size,
        type: wordFile.type,
      });
      fileMapping.push({ type: 'word', file: wordFile });
    }
    if (confidentialFile) {
      fileMetadata.push({
        name: confidentialFile.name,
        size: confidentialFile.size,
        type: confidentialFile.type,
      });
      fileMapping.push({ type: 'confidential', file: confidentialFile });
    }

    // Process attachments metadata
    const attachmentMetadata = [];
    attachments.forEach((attachment) => {
      if (attachment) {
        const { privateAtt, publicAtt } = attachment;
        const attachmentData = {};

        if (publicAtt) {
          attachmentData.publicAtt = {
            name: publicAtt.name,
            size: publicAtt.size,
            type: publicAtt.type,
          };
        }

        if (privateAtt) {
          attachmentData.privateAtt = {
            name: privateAtt.name,
            size: privateAtt.size,
            type: privateAtt.type,
          };
        }

        attachmentMetadata.push(attachmentData);
      }
    });

    // Prepare payload for backend with metadata
    const preparePayload = {
      ...payloadCopy,
      files: fileMetadata,
      attachments: attachmentMetadata,
      useDirectS3Upload: true,
      idempotencyKey, // Add idempotency key
    };

    try {
      // STEP 1: Prepare S3 upload - create discrepancy and get presigned URLs
      const prepareResponse = yield apiRequest(
        'api/v1/discrepancies',
        {
          method: 'post',
          body: JSON.stringify({ jsonBody: JSON.stringify(preparePayload) }),
          headers: {
            'Content-Type': 'application/json',
          },
        },
        true,
        true,
      );

      if (prepareResponse.error) {
        yield put(apiSuccess(commonsTypes.CREATE_DISCREPANCY_FAIL, prepareResponse));
        yield put(apiSuccess(APP.SET_ERROR, { msg: prepareResponse.errorMsg }));
        return;
      }

      const { uploadUrls, newDiscrepancyId, alreadyProcessed } = prepareResponse;

      // Check if this was a duplicate request
      if (alreadyProcessed) {
        console.log('[IDEMPOTENCY] Discrepancy already exists, skipping upload');
        yield put(apiSuccess(commonsTypes.CREATE_DISCREPANCY_SUCCESS, prepareResponse));
        yield put(
          apiSuccess(commonsTypes.OPEN_GENERAL_ALERT, {
            title: 'Aviso',
            message: 'La discrepancia ya fue creada anteriormente',
            status: 'info',
          }),
        );
        return;
      }

      // STEP 2: Upload files directly to S3 - SEQUENTIAL UPLOAD FOR MAIN FILES
      const uploadResults = [];

      // Flatten attachment files into a single array with metadata
      const attachmentFiles = [];
      attachments.forEach((attachment) => {
        if (attachment) {
          if (attachment.publicAtt) {
            attachmentFiles.push({
              file: attachment.publicAtt,
              isPublic: true,
              isPrivate: false,
            });
          }
          if (attachment.privateAtt) {
            attachmentFiles.push({
              file: attachment.privateAtt,
              isPublic: false,
              isPrivate: true,
            });
          }
        }
      });

      console.log('[UPLOAD] Starting file upload process');
      console.log('[UPLOAD] Main file mapping:', fileMapping.map(f => ({ type: f.type, name: f.file.name })));
      console.log('[UPLOAD] Attachment files:', attachmentFiles.map(af => ({ name: af.file.name, isPublic: af.isPublic })));

      // Helper function to upload a single file with retry and exponential backoff
      const uploadFileWithRetry = function* (urlInfo, file, config = DEFAULT_RETRY_CONFIG) {
        const { maxRetries } = config;
        const startTime = Date.now();

        for (let attempt = 0; attempt < maxRetries; attempt++) {
          try {
            console.log(`[UPLOAD] Attempt ${attempt + 1}/${maxRetries} - Uploading ${file.name} to S3`);

            const uploadResult = yield call(
              uploadAndVerifyToS3,
              urlInfo.presignedUrl,
              file,
              urlInfo.contentType,
              file.name
            );

            if (uploadResult.success) {
              console.log(`[UPLOAD] Success: ${file.name}`);
              // Report success if it took retries
              if (attempt > 0) {
                reportUploadSuccess({
                  operation: UploadErrorType.S3_UPLOAD,
                  fileName: file.name,
                  attempt: attempt + 1,
                  totalTime: Date.now() - startTime,
                });
              }
              return {
                attachmentId: urlInfo.attachmentId,
                s3Key: urlInfo.s3Key,
                uploadSuccess: true,
              };
            }

            console.error(`[UPLOAD] Attempt ${attempt + 1} failed:`, uploadResult.error);

            // Report error to Sentry
            reportUploadError({
              operation: UploadErrorType.S3_UPLOAD,
              fileName: file.name,
              fileSize: file.size,
              contentType: urlInfo.contentType,
              attempt: attempt + 1,
              maxRetries,
              error: uploadResult.error,
              attachmentId: urlInfo.attachmentId,
              discrepancyId: newDiscrepancyId,
              isFinalAttempt: attempt === maxRetries - 1,
            });

            // Check if error is recoverable
            const recoverable = isRecoverableError(uploadResult.error);
            if (!recoverable && attempt > 0) {
              console.warn(`[UPLOAD] Non-recoverable error detected, stopping retries`);
              return {
                attachmentId: urlInfo.attachmentId,
                s3Key: urlInfo.s3Key,
                uploadSuccess: false,
                error: uploadResult.error,
              };
            }

            if (attempt < maxRetries - 1) {
              // Wait before retry with exponential backoff and jitter
              const delayMs = calculateBackoffDelay(attempt, config);
              console.log(`[UPLOAD] Waiting ${Math.round(delayMs)}ms before retry...`);
              yield call(sleep, delayMs);
            }
          } catch (error) {
            console.error(`[UPLOAD] Attempt ${attempt + 1} error:`, error);

            // Report error to Sentry
            reportUploadError({
              operation: UploadErrorType.S3_UPLOAD,
              fileName: file.name,
              fileSize: file.size,
              contentType: urlInfo.contentType,
              attempt: attempt + 1,
              maxRetries,
              error,
              attachmentId: urlInfo.attachmentId,
              discrepancyId: newDiscrepancyId,
              isFinalAttempt: attempt === maxRetries - 1,
            });

            if (attempt === maxRetries - 1) {
              return {
                attachmentId: urlInfo.attachmentId,
                s3Key: urlInfo.s3Key,
                uploadSuccess: false,
                error: error.message,
              };
            }

            // Wait before retry with exponential backoff
            const delayMs = calculateBackoffDelay(attempt, config);
            console.log(`[UPLOAD] Waiting ${Math.round(delayMs)}ms before retry...`);
            yield call(sleep, delayMs);
          }
        }

        return {
          attachmentId: urlInfo.attachmentId,
          s3Key: urlInfo.s3Key,
          uploadSuccess: false,
          error: `Máximo de ${maxRetries} reintentos alcanzado`,
        };
      };

      // PHASE 1: Upload main files SEQUENTIALLY
      const mainFileUrls = uploadUrls.filter(u => u.isMainFile);
      let attachmentFileIndex = 0;

      console.log('[UPLOAD] Phase 1: Uploading main files sequentially');
      for (const urlInfo of mainFileUrls) {
        const mappedFile = fileMapping[urlInfo.fileIndex];

        if (!mappedFile || !mappedFile.file) {
          console.error(`[UPLOAD] File not found for index ${urlInfo.fileIndex}`);

          // ROLLBACK: Delete entire discrepancy if main file not found
          yield apiRequest(
            `api/v1/discrepancies/${newDiscrepancyId}`,
            { method: 'delete' },
            false,
          );

          yield put(apiSuccess(commonsTypes.CREATE_DISCREPANCY_FAIL, {}));
          yield put(
            apiSuccess(APP.SET_ERROR, {
              msg: 'Error: Archivo principal no encontrado. Por favor intente nuevamente.',
            }),
          );
          return;
        }

        console.log(`[UPLOAD] Uploading main file: ${mappedFile.type} - ${mappedFile.file.name}`);
        const result = yield* uploadFileWithRetry(urlInfo, mappedFile.file);
        uploadResults.push(result);

        // If main file failed, abort entire upload and rollback
        if (!result.uploadSuccess) {
          console.error(`[UPLOAD] CRITICAL: Main file ${mappedFile.type} failed to upload. Rolling back.`);

          // ROLLBACK: Delete entire discrepancy
          yield apiRequest(
            `api/v1/discrepancies/${newDiscrepancyId}`,
            { method: 'delete' },
            false,
          );

          yield put(apiSuccess(commonsTypes.CREATE_DISCREPANCY_FAIL, {}));
          yield put(
            apiSuccess(APP.SET_ERROR, {
              msg: `Error crítico: No se pudo subir el archivo ${mappedFile.type}. ${result.error || 'Verifica tu conexión e intenta nuevamente.'}`,
            }),
          );
          return;
        }
      }

      console.log('[UPLOAD] Phase 1 completed: All main files uploaded successfully');

      // PHASE 2: Upload attachment files (can fail without aborting)
      const attachmentFileUrls = uploadUrls.filter(u => !u.isMainFile);

      console.log('[UPLOAD] Phase 2: Uploading attachment files');
      for (const urlInfo of attachmentFileUrls) {
        const attachmentFile = attachmentFiles[attachmentFileIndex];
        attachmentFileIndex++;

        if (!attachmentFile || !attachmentFile.file) {
          console.warn(`[UPLOAD] Attachment file not found at index ${attachmentFileIndex - 1}`);
          uploadResults.push({
            attachmentId: urlInfo.attachmentId,
            s3Key: urlInfo.s3Key,
            uploadSuccess: false,
            error: 'Attachment file not found',
          });
          continue;
        }

        console.log(`[UPLOAD] Uploading attachment: ${attachmentFile.file.name}`);
        const result = yield* uploadFileWithRetry(urlInfo, attachmentFile.file);
        uploadResults.push(result);
      }

      console.log('[UPLOAD] Phase 2 completed');
      console.log('[UPLOAD] Upload summary:', {
        total: uploadResults.length,
        successful: uploadResults.filter(r => r.uploadSuccess).length,
        failed: uploadResults.filter(r => !r.uploadSuccess).length,
      });

      // STEP 3: Confirm all uploads with backend (validates S3 existence) - WITH RETRY
      console.log('[CONFIRM] Confirming uploads with backend');

      // Helper function to confirm uploads with retry
      const confirmWithRetry = function* (config = DEFAULT_RETRY_CONFIG) {
        const { maxRetries } = config;
        const startTime = Date.now();

        for (let attempt = 0; attempt < maxRetries; attempt++) {
          try {
            console.log(`[CONFIRM] Attempt ${attempt + 1}/${maxRetries}`);

            const response = yield apiRequest('api/v1/discrepancies/confirm-s3-upload', {
              method: 'post',
              body: JSON.stringify({
                discrepancyId: newDiscrepancyId,
                uploadedFiles: uploadResults,
              }),
              headers: {
                'Content-Type': 'application/json',
              },
            }, true, true);

            if (!response.error) {
              console.log(`[CONFIRM] Success on attempt ${attempt + 1}`);
              // Report success if it took retries
              if (attempt > 0) {
                reportUploadSuccess({
                  operation: UploadErrorType.CONFIRMATION,
                  fileName: `discrepancy_${newDiscrepancyId}`,
                  attempt: attempt + 1,
                  totalTime: Date.now() - startTime,
                });
              }
              return { success: true, data: response };
            }

            console.error(`[CONFIRM] Attempt ${attempt + 1} failed:`, response.errorMsg);

            // Report error to Sentry
            reportUploadError({
              operation: UploadErrorType.CONFIRMATION,
              fileName: `discrepancy_${newDiscrepancyId}`,
              fileSize: 0,
              contentType: 'application/json',
              attempt: attempt + 1,
              maxRetries,
              error: response.errorMsg,
              discrepancyId: newDiscrepancyId,
              isFinalAttempt: attempt === maxRetries - 1,
            });

            // Check if error is recoverable (network issues, timeouts, etc.)
            const recoverable = isRecoverableError(response.errorMsg);

            if (attempt < maxRetries - 1 && recoverable) {
              const delayMs = calculateBackoffDelay(attempt, config);
              console.log(`[CONFIRM] Waiting ${Math.round(delayMs)}ms before retry...`);
              yield call(sleep, delayMs);
            } else if (!recoverable) {
              // Non-recoverable error, don't retry
              console.warn('[CONFIRM] Non-recoverable error, stopping retries');
              return { success: false, error: response.errorMsg, data: response };
            }
          } catch (error) {
            console.error(`[CONFIRM] Attempt ${attempt + 1} exception:`, error);

            // Report error to Sentry
            reportUploadError({
              operation: UploadErrorType.CONFIRMATION,
              fileName: `discrepancy_${newDiscrepancyId}`,
              fileSize: 0,
              contentType: 'application/json',
              attempt: attempt + 1,
              maxRetries,
              error,
              discrepancyId: newDiscrepancyId,
              isFinalAttempt: attempt === maxRetries - 1,
            });

            if (attempt < maxRetries - 1) {
              const delayMs = calculateBackoffDelay(attempt, config);
              console.log(`[CONFIRM] Waiting ${Math.round(delayMs)}ms before retry...`);
              yield call(sleep, delayMs);
            } else {
              return { success: false, error: error.message };
            }
          }
        }

        return { success: false, error: `Máximo de ${maxRetries} reintentos alcanzado` };
      };

      const confirmResult = yield* confirmWithRetry();

      // Check if there were failed attachments (non-critical files)
      const failedAttachments = uploadResults.filter(r => !r.uploadSuccess && uploadUrls.find(u => u.attachmentId === r.attachmentId && !u.isMainFile));

      if (!confirmResult.success) {
        console.error('[CONFIRM] Validation failed after retries:', confirmResult.error);

        // If confirmation fails, show error but don't rollback (files might be partially uploaded)
        yield put(apiSuccess(commonsTypes.CREATE_DISCREPANCY_SUCCESS, prepareResponse));
        yield put(
          apiSuccess(commonsTypes.OPEN_GENERAL_ALERT, {
            title: 'Discrepancia creada con advertencias',
            message: `La discrepancia fue creada pero hubo problemas validando los archivos. ${confirmResult.error || 'Verifica los archivos en el detalle de la discrepancia.'}`,
            status: 'warning',
          }),
        );
        return;
      }

      console.log('[CONFIRM] All uploads confirmed successfully');

      // Show success with warnings if some non-critical attachments failed
      if (failedAttachments.length > 0) {
        const failedFileNames = failedAttachments.map((result) => {
          const urlInfo = uploadUrls.find(u => u.attachmentId === result.attachmentId);
          return urlInfo?.fileName || 'archivo desconocido';
        }).join(', ');

        yield put(apiSuccess(commonsTypes.CREATE_DISCREPANCY_SUCCESS, prepareResponse));
        yield put(
          apiSuccess(commonsTypes.OPEN_GENERAL_ALERT, {
            title: 'Discrepancia creada con advertencias',
            message: `La discrepancia fue creada exitosamente, pero ${failedAttachments.length} archivo(s) adjunto(s) no se pudieron subir: ${failedFileNames}. Puedes agregarlos posteriormente desde el detalle de la discrepancia.`,
            status: 'warning',
          }),
        );
      } else {
        // Complete success
        yield put(apiSuccess(commonsTypes.CREATE_DISCREPANCY_SUCCESS, prepareResponse));
        yield put(
          apiSuccess(commonsTypes.OPEN_GENERAL_ALERT, {
            title: 'Aviso',
            message: 'Creación de discrepancia exitosa',
            status: 'success',
          }),
        );
      }
    } catch (err) {
      yield put(apiSuccess(commonsTypes.CREATE_DISCREPANCY_FAIL, {}));
      yield put(
        apiSuccess(APP.SET_ERROR, {
          msg: err.message || 'Error creando discrepancia',
        }),
      );
    }
  });
}

function* postNaturalEntity() {
  yield takeLatest(commonsTypes.CREATE_NATURAL_ENTITY, function* postNaturalEntityApi(action) {
    const body = { ...action.payload };

    const successFunction = (result) => ({
      type: commonsTypes.CREATE_NATURAL_ENTITY_SUCCESS,
      payload: result,
    });

    const failFunction = () => ({
      type: commonsTypes.CREATE_NATURAL_ENTITY_FAIL,
    });

    const response = yield controlledCall(
      post,
      'api/v1/entities/naturalEntity',
      body,
      successFunction,
      failFunction,
    );

    if (response.error) {
      yield put(apiSuccess(APP.SET_ERROR, { msg: response.errorMsg }));
    } else {
      yield put(
        apiSuccess(commonsTypes.OPEN_GENERAL_ALERT, {
          title: 'Aviso',
          message: 'Creación de persona natural exitosa',
          status: 'success',
        }),
      );
    }
  });
}

function* postLegalEntity() {
  yield takeLatest(commonsTypes.CREATE_LEGAL_ENTITY, function* postLegalEntityApi(action) {
    const payloadCopy = { ...action.payload };
    const body = new FormData();

    const { legalPowersArray } = payloadCopy;

    delete payloadCopy.legalPowersArray;

    const stringifyPayload = JSON.stringify(payloadCopy);

    legalPowersArray.forEach((legalPower) => {
      if (legalPower) {
        body.append('file', legalPower || new Blob(), legalPower.name);
      }
    });

    body.append('jsonBody', stringifyPayload);

    const response = yield apiRequest(
      'api/v1/entities/legalEntity',
      { method: 'post', body },
      false,
    );

    if (!response.error) {
      const { legalEntityFiles, legalEntities } = response.objects;
      const legalEntityCreated = Object.values(legalEntities)[0];
      for (const legalPower of legalPowersArray) {
        const legaEntityFileInfo = Object.values(legalEntityFiles).filter(
          (legalEntityFile) => legalPower.name === legalEntityFile.content,
        )[0];
        try {
          let body = JSON.stringify({
            fileName: legalPower.name,
            attachment: false,
            fileType: 'pdf',
            id: legaEntityFileInfo.id,
          });

          const signedUrlResponse = yield apiRequest(
            `api/v1/s3/get_presigned_url`,
            { method: 'post', body },
            true,
            true,
          );
          if (signedUrlResponse?.error) {
            throw new Error('Error obteniendo signedUrl para guardar el archivo');
          }
          yield fetch(decodeURI(signedUrlResponse?.signedUrl), {
            method: 'put',
            headers: {
              'Content-Type': 'application/pdf',
              'Access-Control-Allow-Headers': 'Content-Type',
              'Access-Control-Allow-Methods': 'OPTIONS,POST,GET',
              'Access-Control-Allow-Origin': '*',
            },
            body: legalPower,
          });
        } catch (error) {
          console.log('ERROR AL INTENTAR SUBIR ARCHIVO');
          yield apiRequest(`api/v1/entities/legalEntity/recentlyCreated/${legalEntityCreated.id}`, {
            method: 'delete',
          });
          yield put(apiSuccess(commonsTypes.CREATE_LEGAL_ENTITY_FAIL, response));
          yield put(
            apiSuccess(APP.SET_ERROR, {
              msg: 'Hubo un error al subir los archivos de la persona jurídica',
            }),
          );
          return;
        }
      }
      yield put(apiSuccess(commonsTypes.CREATE_LEGAL_ENTITY_SUCCESS, response));
      yield put(
        apiSuccess(commonsTypes.OPEN_GENERAL_ALERT, {
          title: 'Aviso',
          message: 'Creación de persona juridica exitosa',
          status: 'success',
        }),
      );
    } else {
      yield put(apiSuccess(commonsTypes.CREATE_LEGAL_ENTITY_FAIL, response));
      yield put(apiSuccess(APP.SET_ERROR, { msg: response.errorMsg }));
    }
  });
}

function* fetchInitialState() {
  yield takeLatest(FETCH_INITIAL_STATE, function* getUserApi() {
    const response = yield apiRequest('api/v1/home', { method: 'get' }, true, true);
    if (response && !response.error) {
      yield put(apiSuccess(FETCH_INITIAL_STATE_SUCCESS, response));
    } else {
      localStorage.removeItem('user');
      yield put(apiSuccess(FETCH_INITIAL_STATE_FAIL));
    }
  });
}

function* deleteDiscrepancy() {
  yield takeLatest(commonsTypes.DELETE_DISCREPANCY, function* deleteDiscrepancyGenerator(action) {
    const response = yield apiRequest(
      `api/v1/discrepancies/${action.payload.discrepancyId}`,
      { method: 'delete' },
      false,
    );

    if (!response.error) {
      yield put(apiSuccess(commonsTypes.DELETE_DISCREPANCY_SUCCESS, response));
      yield put(apiSuccess(APP.SET_SUCCESS, { msg: 'Eliminación de discrepancia exitosa' }));
      action.payload.history.push('/');
    } else {
      yield put(apiSuccess(commonsTypes.DELETE_DISCREPANCY_FAIL, response));
      yield put(apiSuccess(APP.SET_ERROR, { msg: 'Falló al eliminar la discrepancia' }));
    }
  });
}

function* updateDiscrepancy() {
  yield takeLatest(commonsTypes.UPDATE_DISCREPANCY, function* updateDiscrepancyData(action) {
    const payloadCopy = { ...action.payload };

    const body = new FormData();

    const {
      presentation,
      wordPresentation,
      discrepancyId,
      currentPdfPresentation,
      currentWordPresentation,
      oldValues,
    } = payloadCopy;
    delete payloadCopy.presentation;
    delete payloadCopy.wordPresentation;
    delete payloadCopy.currentPdfPresentation;
    delete payloadCopy.currentWordPresentation;
    delete payloadCopy.oldValues;

    const files = [presentation, wordPresentation];

    const oldFiles = [currentPdfPresentation, currentWordPresentation];

    const stringifyPayload = JSON.stringify(payloadCopy);

    files.forEach((file) => {
      if (file) {
        body.append('files[]', file, `${file.name?.replace(/[\/\\:*?<>|()\[\]{}"]/g, '')}`);
      }
    });

    body.append('jsonBody', stringifyPayload);

    const response = yield apiRequest(
      `api/v1/discrepancies/${discrepancyId}`,
      { method: 'put', body },
      false,
    );

    if (!response.error) {
      const filesUpdated = Object.values(response.objects.attachments);
      for (const fileTest of filesUpdated) {
        if (fileTest) {
          const { contentType, id, content } = fileTest;
          const file = files.filter((file) => file.name?.replace(/[\/\\:*?<>|()\[\]{}"]/g, '') === fileTest.content);
          let body = JSON.stringify({
            fileName: content?.replace(/[\/\\:*?<>|()\[\]{}"]/g, ''),
            attachment: true,
            fileType: contentType,
            id,
          });
          try {
            const signedUrlResponse = yield call(
              apiRequest,
              `api/v1/s3/get_presigned_url`,
              { method: 'post', body },
              true,
              true,
            );
            if (signedUrlResponse?.error) {
              throw new Error('Error obteniendo signedUrl para guardar el archivo');
            }
            yield fetch(decodeURI(signedUrlResponse?.signedUrl), {
              method: 'put',
              headers: {
                'Content-Type': contentType,
                'Access-Control-Allow-Headers': 'Content-Type',
                'Access-Control-Allow-Methods': 'OPTIONS,POST,GET',
                'Access-Control-Allow-Origin': '*',
              },
              body: file[0],
            });
            body = JSON.stringify({
              id,
              fileName: content?.replace(/[\/\\:*?<>|()\[\]{}"]/g, ''),
              contentType,
              attachment: true,
            });
            const viewFileSignedUrlResponse = yield apiRequest(
              `api/v1/s3/get_presigned_url_view`,
              {
                method: 'post',
                body,
              },
              true,
              true,
            );
            if (viewFileSignedUrlResponse?.error) {
              throw new Error('Error obteniendo signedUrl para guardar el archivo');
            }
            response.objects.attachments[id].url = viewFileSignedUrlResponse.signedUrl;
          } catch (error) {
            const body = new FormData();
            oldFiles.forEach((oldFile) => {
              if (oldFile) {
                body.append('files[]', new Blob(), `${oldFile.content?.replace(/[\/\\:*?<>|()\[\]{}"]/g, '')}`);
              }
            });
            body.append('jsonBody', JSON.stringify(oldValues));
            const response = yield apiRequest(
              `api/v1/discrepancies/${discrepancyId}`,
              { method: 'put', body },
              false,
            );
            yield put(apiSuccess(commonsTypes.UPDATE_DISCREPANCY_FAIL));
            yield put(
              apiSuccess(APP.SET_ERROR, { msg: 'Error al intentar subir archivo a la plataforma' }),
            );

            console.log('ERROR AL INTENTAR SUBIR EL ARCHIVO');
            return;
          }
        }
      }
      yield put(apiSuccess(commonsTypes.UPDATE_DISCREPANCY_SUCCESS, response));
      yield put(apiSuccess(APP.SET_SUCCESS, { msg: 'Discrepancia actualizada correctamente.' }));
    } else {
      yield put(apiSuccess(commonsTypes.UPDATE_DISCREPANCY_FAIL, response));
      yield put(apiSuccess(APP.SET_ERROR, { msg: response.errorMsg }));
    }
  });
}

function* updateNaturalEntity() {
  yield takeLatest(commonsTypes.EDIT_NATURAL_ENTITY, function* updateNaturalEntityApi(action) {
    const body = { ...action.payload };
    const successFunction = (result) => ({
      type: commonsTypes.EDIT_NATURAL_ENTITY_SUCCESS,
      payload: result,
    });

    const failFunction = () => ({
      type: commonsTypes.EDIT_NATURAL_ENTITY_FAIL,
    });

    const response = yield controlledCall(
      update,
      `api/v1/entities/naturalEntity/${body.userId}`,
      body,
      successFunction,
      failFunction,
    );

    if (response.error) {
      yield put(apiSuccess(APP.SET_ERROR, { msg: response.errorMsg }));
    } else {
      yield put(appActions.fetchInitialState());
    }
  });
}

function* updateLegalEntity() {
  yield takeLatest(commonsTypes.EDIT_LEGAL_ENTITY, function* updateNaturalEntityApi(action) {
    const body = { ...action.payload };
    const successFunction = (result) => ({
      type: commonsTypes.EDIT_LEGAL_ENTITY_SUCCESS,
      payload: result,
    });

    const failFunction = () => ({
      type: commonsTypes.EDIT_LEGAL_ENTITY_FAIL,
    });

    const response = yield controlledCall(
      update,
      `api/v1/entities/legalEntity/${body.userId}`,
      body,
      successFunction,
      failFunction,
    );

    if (response.error) {
      yield put(apiSuccess(APP.SET_ERROR, { msg: response.errorMsg }));
    } else {
      yield put(appActions.fetchInitialState());
    }
  });
}

function* deleteLegalPower() {
  yield takeLatest(commonsTypes.DELETE_LEGAL_POWER, function* deleteLegalPowerApi(action) {
    const { powerLegalFileId } = action.payload;

    const response = yield apiRequest(
      `api/v1/legalEntities/${powerLegalFileId}`,
      { method: 'delete' },
      false,
    );

    if (!response.error) {
      yield put(apiSuccess(commonsTypes.DELETE_LEGAL_POWER_SUCCESS, response));
      yield put(
        apiSuccess(APP.SET_SUCCESS, { msg: 'Eliminación de poder de representación exitosa' }),
      );
    } else {
      yield put(apiSuccess(APP.SET_ERROR, { msg: response.errorMsg }));
    }
  });
}

function* createLegalPower() {
  yield takeLatest(commonsTypes.CREATE_LEGAL_POWER, function* createLegalPowerApi(action) {
    const { legalPowerFile, userId } = action.payload;
    const formData = new FormData();
    if (legalPowerFile?.type !== 'application/pdf') {
      yield put(apiSuccess(commonsTypes.CREATE_LEGAL_ENTITY_FAIL));
      yield put(
        apiSuccess(APP.SET_ERROR, {
          msg: 'Los archivos de persona jurídica deben ser archivos PDF',
        }),
      );
      return;
    }

    formData.append('file', legalPowerFile, legalPowerFile.name);

    const response = yield apiRequest(
      `api/v1/legalEntities/${userId}`,
      { method: 'post', body: formData },
      false,
    );

    if (!response.error) {
      const legalEntityFileCreated = Object.values(response.objects.legalEntityFiles)[0];
      try {
        let body = JSON.stringify({
          fileName: legalPowerFile.name,
          attachment: false,
          fileType: 'pdf',
          id: legalEntityFileCreated.id,
        });

        const signedUrlResponse = yield apiRequest(
          `api/v1/s3/get_presigned_url`,
          { method: 'post', body },
          true,
          true,
        );
        if (signedUrlResponse?.error) {
          throw new Error('Error obteniendo signedUrl para guardar el archivo');
        }
        yield fetch(decodeURI(signedUrlResponse?.signedUrl), {
          method: 'put',
          headers: {
            'Content-Type': 'application/pdf',
            'Access-Control-Allow-Headers': 'Content-Type',
            'Access-Control-Allow-Methods': 'OPTIONS,POST,GET',
            'Access-Control-Allow-Origin': '*',
          },
          body: legalPowerFile,
        });
        body = JSON.stringify({
          id: legalEntityFileCreated.id,
          fileName: legalPowerFile.name,
          contentType: 'pdf',
          attachment: false,
        });
        const viewFileSignedUrlResponse = yield apiRequest(
          `api/v1/s3/get_presigned_url_view`,
          {
            method: 'post',
            body,
          },
          true,
          true,
        );
        if (viewFileSignedUrlResponse?.error) {
          throw new Error('Error obteniendo signedUrl para guardar el archivo');
        }
        response.objects.legalEntityFiles[legalEntityFileCreated.id].url =
          viewFileSignedUrlResponse.signedUrl;
        response.objects.legalEntityFiles[legalEntityFileCreated.id].size = legalPowerFile.size;
      } catch (error) {
        console.log('ERROR AL SUBIR ARCHIVO');
        const response = yield apiRequest(
          `api/v1/legalEntities/${legalEntityFileCreated.id}`,
          { method: 'delete' },
          true,
          true,
        );
        yield put(apiSuccess(commonsTypes.CREATE_LEGAL_ENTITY_FAIL, response));
        yield put(
          apiSuccess(APP.SET_ERROR, { msg: 'Hubo un error al subir el archivo a la plataforma' }),
        );
        return;
      }

      yield put(apiSuccess(commonsTypes.CREATE_LEGAL_POWER_SUCCESS, response));
      yield put(
        apiSuccess(APP.SET_SUCCESS, { msg: 'Creación de poder de representación exitoso' }),
      );
    } else {
      yield put(apiSuccess(commonsTypes.CREATE_LEGAL_ENTITY_FAIL, response));
      yield put(apiSuccess(APP.SET_ERROR, { msg: response.errorMsg }));
    }

    // yield put({ type: commonsTypes.DELETE_LEGAL_POWER_SUCCESS });
  });
}

function* updateDiscrepancyMessage() {
  yield takeLatest(
    commonsTypes.UPDATE_DISCREPANCY_MESSAGE,
    function* updateDiscrepancyMessageApi(action) {
      const body = { ...action.payload };
      const successFunction = (result) => ({
        type: commonsTypes.UPDATE_DISCREPANCY_MESSAGE_SUCCESS,
        payload: result,
      });

      const failFunction = () => ({
        type: commonsTypes.UPDATE_DISCREPANCY_MESSAGE_FAIL,
      });

      const response = yield controlledCall(
        post,
        `api/v1/discrepancies/discrepancy-message`,
        body,
        successFunction,
        failFunction,
      );

      if (response.error) {
        yield put(apiSuccess(APP.SET_ERROR, { msg: response.errorMsg }));
      }
    },
  );
}

function* validateCaptcha() {
  yield takeLatest(APP.VALIDATE_CAPTCHA, function* validateCaptchaApi(action) {
    const { token, secretKey } = action.payload;

    let body = JSON.stringify({
      token,
      secretKey,
    });
    const response = yield apiRequest(
      'api/v1/home/validate-captcha',
      { method: 'post', body },
      true,
      true,
    );

    if (!response.error) {
      yield put(apiSuccess(APP.VALIDATE_CAPTCHA_SUCCESS, response));
    } else {
      yield put(apiSuccess(APP.SET_ERROR, { msg: response.errorMsg }));
    }
  });
}

export default function* CommonsSaga() {
  // yield spawn(logout);
  yield spawn(fetchInitialState);
  yield spawn(postDiscrepancy);
  yield spawn(postNaturalEntity);
  yield spawn(updateDiscrepancy);
  yield spawn(postLegalEntity);
  yield spawn(updateNaturalEntity);
  yield spawn(updateLegalEntity);
  yield spawn(deleteDiscrepancy);
  yield spawn(deleteLegalPower);
  yield spawn(createLegalPower);
  yield spawn(updateDiscrepancyMessage);
  yield spawn(validateCaptcha);
}
